import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import clsx from 'clsx';
import { Grid, GridColumn as Column, GridNoRecords } from '@progress/kendo-react-grid';
import { orderBy, filterBy } from '@progress/kendo-data-query';
import BobTableContextMenu from '../BobTableContextMenu';
import RuleExceptionModal from '../../containers/Modals/RuleExceptionModal';
import moment from 'moment';
import MetaContext from './MetaContext';
import ProgressBar from '../ProgressBar';
import * as ruleActions from '../../actions/ruleActions';
import * as DashboardActions from '../../actions/dashboardActions';
import * as BobActions from '../../actions/bobActions';
import defaultClasses from './RuleExceptionsTable.module.css';
import { getTimeZoneAbbr } from '../../helpers/timezones';
import BobLoadingMask from '../BobLoadingMask';
import { toNumber } from 'lodash';
import { kendoGridInlineStyling } from '../../helpers/kendoGridInlineStyleHelper';

const initialDataState = {
    sort: [],
    take: 20,
    skip: 0
};

const RuleExceptionsTable = ({
    location,
    bobLoading,
    error,
    exceptions,
    fetchRuleDebuggerList,
    isLoading,
    onCurrentPageChange,
    onPageSizeChange,
    totalCount,
    workboookMode
}) => {
    const [ruleDebuggerList, setRuleDebuggerList] = useState([]);
    const [take, setTake] = useState(20);
    const [skip, setSkip] = useState(0);
    const [pageable] = useState({pageSizes: [20, 50, 100]});
    const [dataState, setDataState] = useState(initialDataState);
    const [showExceptionModal, setShowExceptionModal] = useState(false);
    const [ruleException, setRuleException] = useState(null);

    useEffect(() => {
        async function init() {
            const newParams = new URLSearchParams(location.search);
            const pageSize = newParams.get('pageSize');
            const urlPage = newParams.get('page');
            if (pageSize) {
                setTake(pageSize);
                setDataState({...dataState, take: toNumber(pageSize)});
                onPageSizeChange(pageSize);
            }
            if (urlPage) {
                if (pageSize) {
                    const skipCount = (toNumber(urlPage) - 1) * toNumber(pageSize);
                    setSkip(skipCount);
                    setDataState({...dataState, skip: skipCount});
                }
                onCurrentPageChange(urlPage);                
            }
        }
        init();
    }, []);

    useEffect(() => {
        setRuleDebuggerList(exceptions);
    }, [exceptions]);

    let noDataMessage = 'There are no Rule Exceptions in this time period';
    if (isLoading) {
        noDataMessage = 'Loading Rule Exceptions...';
    } else if (error) {
        noDataMessage = `Error Loading Rule Exceptions: ${error}`;
    }


    const timestampCell = props => {
        const zone = moment.tz.guess();
        const displayDate = moment.parseZone(props.dataItem[props.field]).local().format('YYYY-MM-DD h:mm A') + ' ' + getTimeZoneAbbr(props.dataItem[props.field], zone);

        return (
            <td className={props.className} style={props.style}>
                {displayDate}
            </td>
        );
    };

    const noteCell = props => {
        return (
            <td className={clsx(props.className, defaultClasses.NoteCell)} style={props.style}>
                {props.dataItem[props.field]}
            </td>
        );
    };

    const actionsCell = props => {
        return (
            <td className={props.className} style={props.style}>
                <BobTableContextMenu 
                    type="RuleDebugger"
                    entityData={props.dataItem}
                    handleShowDebuggerMessage={(entityData) => {handleShowDebuggerMessage(entityData);}}
                />
            </td>
        );
    };

    const handleShowDebuggerMessage = entityData => {
        setRuleException(entityData);
        setShowExceptionModal(true);
    };

    const handleModalClose = () => {
        setRuleException(null);
        setShowExceptionModal(false);
    };

    const onDataStateChange = async e => {
        if (e.dataState.skip !== skip || e.dataState.take !== take) {
            let pageNumber = 1;

            if (e.dataState.skip > 0) {
                pageNumber = e.dataState.skip / e.dataState.take + 1;
            }

            if (e.dataState.skip !== skip) {
                onCurrentPageChange(pageNumber);                
            }
            else if (e.dataState.take !== take) {
                onCurrentPageChange(1);
                onPageSizeChange(e.dataState.take);
            }
        }
        setSkip(e.dataState.skip);
        setTake(e.dataState.take);
        setDataState(e.dataState);
    };

    return (
        <MetaContext.Provider value={{ error }}>
            <ProgressBar
                className={defaultClasses.progressBar}
                hidden={!isLoading}
            />
            <div>
                <style>
                    { kendoGridInlineStyling }
                </style>
                <div className={defaultClasses.RuleDebuggerTable} id="table">
                    <Grid
                        className={defaultClasses.KendoGrid}
                        sortable
                        filterable={workboookMode}
                        data={ruleDebuggerList.length > 0 ? orderBy(filterBy(ruleDebuggerList, dataState.filter), dataState.sort) : []}
                        {...dataState}
                        take={take}
                        skip={skip}
                        pageable={pageable}
                        onDataStateChange={onDataStateChange}
                        total={totalCount}
                    >
                        <GridNoRecords>
                            {noDataMessage}
                        </GridNoRecords>
                        <Column
                            title="Timestamp"
                            field="insertTimestamp"
                            cell={timestampCell}
                            width="230px"
                            filter={'date'}
                        />
                        <Column
                            title="Client"
                            field="clientName"
                            width="230px"
                        />
                        <Column
                            title="Site"
                            field="siteName"
                            width="230px"
                        />
                        <Column
                            title="Target"
                            field="entityName"
                            width="230px"
                        />
                        <Column
                            title="Message"
                            field="notes"
                            cell={noteCell}
                        />
                        <Column
                            //locked={true}
                            filterable={false}
                            sortable={false}
                            title=""
                            field=""
                            width="75px"
                            cell={actionsCell}
                        />
                    </Grid>
                    {
                        showExceptionModal ? 
                            <RuleExceptionModal
                                exception={ruleException}
                                onConfirm={handleModalClose}
                                onClose={handleModalClose}
                                open={showExceptionModal}
                            />
                            : null        
                    }
                    {
                        bobLoading.includes('fetchRuleDebuggerList') ?
                            <BobLoadingMask />
                            :
                            null
                    }                
                </div>
            </div>
        </MetaContext.Provider>
    );
};

RuleExceptionsTable.defaultProps = {
    exceptions:          [],
    isLoading:           false,
    onCurrentPageChange: () => {},
    onPageSizeChange:    () => {},
    pageSizes:           []
};

RuleExceptionsTable.propTypes = {
    currentPage: number,
    error:       string,
    exceptions:  arrayOf(shape({
        insertTimestamp: string,
        notes:           string,
        ruleName:        string
    })),
    isLoading:           bool,
    onCurrentPageChange: func,
    onPageSizeChange:    func,
    pageSize:            number,
    pageSizes:           arrayOf(number),
    totalCount:          number
};

const mapStateToProps = state => {
    return {
        bobLoading:         state.bob.bobLoading,
        ruleEndTimeStamp:   state.rules.ruleEndTimeStamp,
        ruleStartTimeStamp: state.rules.ruleStartTimeStamp,
    };
};

const mapDispatchToProps = {
    ...BobActions,
    ...DashboardActions,
    ...ruleActions
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RuleExceptionsTable));
